import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { AlertFillIcon } from '@arcade/web/icons/react'
import { ArcadeAvatar, CheckboxInput, Flex, spacing, Stack, Text } from '@cash-design-system/react'
import { getArcadeAvatarFromAvatarProto } from '@cash-web/avatar-from-proto'
import { Color } from '@cash-web/protos/squareup/cash/ui/Color.pb'
import { FormBlockerElementMerchantTransactionElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { moneyFormatter } from '@cash-web/shared-util-formatting'

export const MerchantTransactionElementContainer = styled.div<{ isSelected: boolean; selectable: boolean }>`
  border-radius: 1rem;
  display: grid;
  grid-template-columns: 1fr ${props => (props.selectable ? '1.25rem' : '2.75rem')};
  padding: ${({ theme }) => theme.spacing.m};
  border: 0.125rem solid ${props => props.theme.colors.formFieldBackground};

  svg > path {
    color: ${props => props.theme.colors.error};
  }
  ${props =>
    props.isSelected &&
    css`
      border-color: ${props.theme.colors.primary};
    `}
  ${props =>
    props.selectable &&
    css`
      cursor: pointer;
    `}

  font: inherit;
  outline: inherit;
  user-select: none;
  ${({ theme }) => theme.selectors.keyboard} &:focus {
    border-radius: 0.5rem;
    outline: 0.5rem solid ${props => props.theme.colors.primary}33;
  }
`
export const ServerColorText = styled(Text)<{ serverColor?: Color }>`
  ${props => {
    const color = props.theme.darkMode ? props?.serverColor?.dark?.srgb : props?.serverColor?.light?.srgb
    if (color != null) {
      return `color: ${color};`
    }
    return null
  }};
`
export const MerchantTransactionLabel = ({
  merchantName,
  date,
  transactionStatus,
  locationLineOne,
  locationLineTwo,
  avatar,
  avatarBadged,
  amount,
  selectable,
  transactionStatusColor,
}: FormBlockerElementMerchantTransactionElement) => {
  const arcadeAvatarProps = avatar ? getArcadeAvatarFromAvatarProto({ avatarProto: avatar }) : {}
  return (
    <Flex gap={spacing.m}>
      {avatar && (
        <ArcadeAvatar
          {...arcadeAvatarProps}
          size="small"
          {...(avatarBadged ? { overlayIcon: { element: <AlertFillIcon size={24} variant="danger" /> } } : {})}
        />
      )}
      <Stack space={spacing.xxs}>
        <Text typography="labelMedium">{merchantName}</Text>
        <Text typography="labelMedium" color="textSubtle">
          {date}
        </Text>
        {selectable && (
          <Text typography="labelMedium" color="textSubtle">
            {moneyFormatter().formatMoney(amount?.money?.amount)}
          </Text>
        )}
        <Text typography="labelMedium" color="textSubtle">
          {locationLineOne}
        </Text>
        <Text typography="labelMedium" color="textSubtle">
          {locationLineTwo}
        </Text>
        {!selectable && (
          <ServerColorText typography="labelMedium" serverColor={transactionStatusColor}>
            {transactionStatus}
          </ServerColorText>
        )}
      </Stack>
    </Flex>
  )
}
export type MerchantTransactionElementProps = FormBlockerElementMerchantTransactionElement & {
  selected?: boolean
  index?: number
  onChange?: (id?: string) => void
}
export const MerchantTransactionElement = ({
  onChange,
  selected,
  index,
  ...props
}: MerchantTransactionElementProps) => {
  return (
    <MerchantTransactionElementContainer
      data-testid={`merchant-transaction-element-${props.transactionId}`}
      selectable={props.selectable === true}
      isSelected={selected === true}
      role="checkbox"
      tabIndex={index}
      aria-checked={selected}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && props.selectable) {
          onChange?.(props.transactionId)
        }
      }}
      onClick={e => {
        if (props.selectable) {
          onChange?.(props.transactionId)
        }
      }}
    >
      <MerchantTransactionLabel {...props} />
      {props.selectable ? (
        <CheckboxInput
          variant="primary"
          checked={selected}
          label={null}
          onChange={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <Text typography="labelMedium">{moneyFormatter().formatMoney(props.amount?.money?.amount)}</Text>
      )}
    </MerchantTransactionElementContainer>
  )
}
